export type NetworkListener = (online: boolean) => void;

/** Where connectivity comes from: `navigator.onLine` in the browser, a switch in tests. */
export interface NetworkSource {
  readonly online: boolean;
  subscribe(listener: NetworkListener): () => void;
}

export interface NetworkState {
  readonly online: boolean;
  /** Fires only on transitions. */
  subscribe(listener: NetworkListener): () => void;
}

export interface StaticNetworkSource extends NetworkSource {
  set(online: boolean): void;
}

/** A controllable source for tests and for platforms without connectivity events. */
export function staticNetworkSource(online: boolean): StaticNetworkSource {
  const listeners = new Set<NetworkListener>();
  let current = online;
  return {
    get online() {
      return current;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set(next) {
      if (next === current) return;
      current = next;
      for (const l of [...listeners]) l(next);
    },
  };
}

export function createNetworkState(
  source: NetworkSource = staticNetworkSource(true),
): NetworkState {
  return {
    get online() {
      return source.online;
    },
    subscribe(listener) {
      let last = source.online;
      return source.subscribe((online) => {
        if (online === last) return;
        last = online;
        listener(online);
      });
    },
  };
}
